/**
 * useCurrentUser Hook
 *
 * React Query-based session hook that replaces GraphQL Me query.
 * When wrapped in CurrentUserProvider, all components share one query.
 * Falls back to independent query if no provider is present.
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { authKeys } from '../queryKeys';
import { getCurrentUser, type CurrentUserData } from '../actions/getCurrentUser';
import { useCurrentUserContext } from '../components/CurrentUserProvider';

export type CurrentUser = CurrentUserData;

export interface UseCurrentUserResult {
  /** Current user data, null if not authenticated */
  user: CurrentUser | null;
  /** True if user is authenticated */
  isAuthenticated: boolean;
  /** True while loading */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
  /** Refetch user data */
  refetch: () => Promise<void>;
}

/**
 * Get current authenticated user via server action + React Query.
 *
 * Uses shared CurrentUserProvider context (1 query for all consumers).
 * Falls back to independent query if provider is not in the tree.
 */
export function useCurrentUser(): UseCurrentUserResult {
  const ctx = useCurrentUserContext();

  // Independent query (only used when no provider in tree)
  const { data, isLoading, error, refetch } = useQuery<CurrentUser | null>({
    queryKey: authKeys.currentUser,
    queryFn: () => getCurrentUser(),
    // Disable when provider exists (provider handles data)
    enabled: !ctx,
  });

  // If provider exists, use shared data (no extra network requests)
  if (ctx) {
    return ctx;
  }

  return {
    user: data ?? null,
    isAuthenticated: !!data,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refetch: async () => { await refetch(); },
  };
}

/**
 * Hook for components that require authentication
 * Throws if not authenticated (use with Suspense boundary)
 */
function useRequireUser(): CurrentUser {
  const { user, loading, isAuthenticated } = useCurrentUser();

  if (loading) {
    throw new Promise(() => {}); // Suspend
  }

  if (!isAuthenticated || !user) {
    throw new Error('Authentication required');
  }

  return user;
}
